/**
 * Teaching & Learning cluster (Feb 2026 peer-parity ship).
 *
 * Renders nine cards backed by the fields seeded by
 * `backend/scripts/seed_teaching_learning.py` and the benchmarks
 * endpoint `GET /degrees/{id}/teaching-learning-benchmarks`.
 *
 * UK cards (rendered when country === "UK"):
 *   • NSSSubscalesCard         7 horizontal bars + national/subject median markers
 *   • ContinuationCard         Single-figure gauge + benchmark
 *   • ContactHoursCard         Stacked horizontal bar per academic year
 *   • AssessmentSplitCard      Donut (written / coursework / practical) + benchmark row
 *
 * US cards (rendered when country === "US"):
 *   • GradRateCard             6-yr grad rate + retention (twin figures + benchmark)
 *   • NetPriceLadderCard       5 income brackets, one bar per rung
 *   • MedianDebtCard           Big number + benchmark
 *   • Career10yrCard           10-yr median earnings + benchmark
 *   • EquityCard               Pell % / first-gen % / racial composition
 *
 * When the source field is missing / suppressed for a small cohort,
 * the corresponding card is not rendered (the user's explicit ask —
 * "Hide the card entirely"). Cluster returns `null` if *no* card has
 * data, so the section never shows an empty header.
 */

import { useEffect, useState } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import {
  BarChart, Bar, XAxis, YAxis, ReferenceLine, ResponsiveContainer,
  Cell, PieChart, Pie, Tooltip, LabelList,
} from "recharts";
import EarningsTrajectoryCard from "./EarningsTrajectoryCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Palette — matches the rest of the app's editorial navy/plum.
const C_PRIMARY  = "#002147";  // Oxford navy — course's own value
const C_NATIONAL = "#94a3b8";  // slate — national benchmark
const C_SUBJECT  = "#7C3AED";  // plum — subject-level benchmark
const C_MUTED    = "#e5e7eb";

const fmtUSD = (n) => (typeof n === "number"
  ? `$${Math.round(n).toLocaleString()}`
  : "—");
const fmtPct = (n, digits = 1) => (typeof n === "number"
  ? `${n.toFixed(digits)}%`
  : "—");

// ---------------------------------------------------------------------------
// Card wrapper — one editorial style for every card in the cluster.
//
// `sourceBlock` (optional): the sub-object that carries `data_source`
// + `data_year`. When `data_source === "seed_estimate"` we render an
// amber "Seeded estimate" pill in the footer; otherwise a green
// "Verified · <source> <year>" pill. Cards that don't need
// verification pass `null`.
// ---------------------------------------------------------------------------
const VerifiedPill = ({ block }) => {
  if (!block) return null;
  const src = block.data_source;
  const yr  = block.data_year;
  if (!src) return null;
  const isSeed = src === "seed_estimate";
  return (
    <span
      data-testid={`source-pill-${isSeed ? "seed" : "verified"}`}
      title={isSeed
        ? "Seeded estimate — real-source feed pending operator drop"
        : `${src}${yr ? ` (${yr})` : ""}`}
      className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-underground-sc tracking-wider ${
        isSeed ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"
      }`}
    >
      {isSeed ? "Seeded estimate" : `Verified · ${src.replace(/\s*\(.*?\)\s*/g, "").replace(/\s+\d{4,6}(?:[/-]\d{2,4})?\s*$/, "").slice(0, 32)}${yr ? ` ${yr}` : ""}`}
    </span>
  );
};

const CardShell = ({ testid, kicker, title, subtitle, children, footer, sourceBlock }) => (
  <Card
    data-testid={testid}
    className="p-6 border border-stone-200 rounded-none shadow-none bg-white"
  >
    {kicker ? (
      <div className="text-[10px] font-underground-sc tracking-wider text-[#7C3AED] mb-1">
        {kicker}
      </div>
    ) : null}
    <h3 className="font-editorial text-xl text-stone-900 leading-tight">{title}</h3>
    {subtitle ? (
      <p className="text-xs text-stone-500 mt-1 mb-4">{subtitle}</p>
    ) : <div className="mb-4" />}
    {children}
    {(footer || sourceBlock) ? (
      <div className="flex flex-wrap items-center gap-2 mt-4 pt-3 border-t border-stone-100">
        {footer ? (
          <span className="text-[10px] font-underground-sc tracking-wider text-stone-400">
            {footer}
          </span>
        ) : null}
        <VerifiedPill block={sourceBlock} />
      </div>
    ) : null}
  </Card>
);

// Small legend used above bar charts to explain the three colours.
const LegendRow = () => (
  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] font-underground-sc tracking-wider text-stone-500 mb-2">
    <span className="inline-flex items-center gap-1.5">
      <span className="w-3 h-3 rounded-sm" style={{ background: C_PRIMARY }} />
      This course
    </span>
    <span className="inline-flex items-center gap-1.5">
      <span className="w-3 h-3 rounded-sm" style={{ background: C_SUBJECT }} />
      Subject median
    </span>
    <span className="inline-flex items-center gap-1.5">
      <span className="w-3 h-3 rounded-sm" style={{ background: C_NATIONAL }} />
      National median
    </span>
  </div>
);

// ---------------------------------------------------------------------------
// UK · NSS sub-scales — 7 horizontal bars, "This course" primary + subject
// median dot + national median dot on the same row.
// ---------------------------------------------------------------------------
const NSS_LABELS = {
  teaching:     "Teaching",
  assessment:   "Assessment & feedback",
  resources:    "Learning resources",
  support:      "Academic support",
  personal_dev: "Personal development",
  community:    "Learning community",
  voice:        "Student voice",
};
function NSSSubscalesCard({ degree, benchmarks }) {
  const nss = degree?.nss_subscales;
  if (!nss) return null;

  const rows = Object.keys(NSS_LABELS).map((k) => ({
    key: k,
    label: NSS_LABELS[k],
    value: nss[k],
    subject:  benchmarks?.subject?.nss?.[k]  ?? null,
    national: benchmarks?.national?.nss?.[k] ?? null,
  })).filter((r) => typeof r.value === "number");

  if (!rows.length) return null;

  return (
    <CardShell
      testid="nss-subscales-card"
      kicker="Student Voice"
      title="How students actually rate the teaching"
      subtitle={`Each bar is the % of finalists in the ${nss.year} National Student Survey who agreed with the statement. Markers show subject & national medians.`}
      footer={`Source: OfS NSS ${nss.year} · cohort ${nss.cohort_size ?? "—"}`}
      sourceBlock={nss}
    >
      <LegendRow />
      <div className="space-y-2.5">
        {rows.map((r) => (
          <div key={r.key} data-testid={`nss-row-${r.key}`}>
            <div className="flex items-baseline justify-between text-xs text-stone-700 mb-0.5">
              <span>{r.label}</span>
              <span className="font-mono text-[11px]">{fmtPct(r.value, 0)}</span>
            </div>
            <div className="relative h-4 bg-stone-100 rounded">
              {/* National median tick */}
              {typeof r.national === "number" ? (
                <div
                  className="absolute top-0 bottom-0 w-[2px] bg-stone-400"
                  style={{ left: `${r.national}%` }}
                  title={`National median: ${r.national}%`}
                />
              ) : null}
              {/* Subject median tick */}
              {typeof r.subject === "number" ? (
                <div
                  className="absolute top-0 bottom-0 w-[2px]"
                  style={{ left: `${r.subject}%`, background: C_SUBJECT }}
                  title={`Subject median: ${r.subject}%`}
                />
              ) : null}
              {/* Course value bar */}
              <div
                className="absolute top-0.5 bottom-0.5 rounded-sm"
                style={{ left: 0, width: `${Math.min(100, r.value)}%`, background: C_PRIMARY, opacity: 0.9 }}
              />
            </div>
          </div>
        ))}
      </div>
    </CardShell>
  );
}

// ---------------------------------------------------------------------------
// UK · Continuation rate — one big number + delta vs benchmark.
// ---------------------------------------------------------------------------
function ContinuationCard({ degree, benchmarks }) {
  const cont = degree?.continuation;
  const val = cont?.continuing_pct ?? cont?.first_year_retention_pct;
  if (typeof val !== "number") return null;
  const isVerified = cont?.data_source && cont.data_source !== "seed_estimate";
  const nat = benchmarks?.national?.continuation;
  const sub = benchmarks?.subject?.continuation;
  const delta = typeof sub === "number" ? val - sub : (typeof nat === "number" ? val - nat : null);
  const arrow = delta == null ? "" : (delta > 0.5 ? "▲" : (delta < -0.5 ? "▼" : "▬"));
  const arrowClr = delta == null ? "text-stone-500" : (delta > 0.5 ? "text-emerald-700" : (delta < -0.5 ? "text-red-700" : "text-stone-500"));
  return (
    <CardShell
      testid="continuation-card"
      kicker="Retention"
      title="Do first-years stay?"
      subtitle={
        isVerified
          ? "Percentage of first-years who continued or qualified — the OfS-standardised drop-out indicator from Discover Uni."
          : "Percentage of students who continue into their second year — HESA's core drop-out indicator."
      }
      footer={
        isVerified
          ? `Source: ${cont.data_source}${cont.cohort_pop ? ` · n=${cont.cohort_pop.toLocaleString()}` : ""}`
          : `Source: HESA UK Performance Indicators ${cont.year ?? ""}`
      }
      sourceBlock={cont}
    >
      <div className="flex items-baseline gap-4">
        <div className="font-serif text-5xl text-[#002147]">{fmtPct(val, 1)}</div>
        {delta != null ? (
          <div className={`text-sm font-mono ${arrowClr}`} data-testid="continuation-delta">
            {arrow} {delta > 0 ? "+" : ""}{delta.toFixed(1)} pts vs {typeof sub === "number" ? "subject" : "national"} median
          </div>
        ) : null}
      </div>
      {isVerified && typeof cont.left_pct === "number" ? (
        <div className="mt-3 text-[11px] font-underground-sc tracking-wider text-stone-500">
          {fmtPct(cont.left_pct, 1)} left HE · {fmtPct(cont.lower_pct, 1)} moved to lower qual · {fmtPct(cont.gained_pct, 1)} completed early
        </div>
      ) : null}
      <div className="grid grid-cols-2 gap-3 mt-4 text-[11px] font-underground-sc tracking-wider text-stone-500">
        <div>Subject median: <span className="text-stone-800">{fmtPct(sub, 1)}</span></div>
        <div>National median: <span className="text-stone-800">{fmtPct(nat, 1)}</span></div>
      </div>
    </CardShell>
  );
}

// ---------------------------------------------------------------------------
// UK · Contact hours per year — stacked horizontal bar (scheduled / independent / placement).
// ---------------------------------------------------------------------------
function ContactHoursCard({ degree }) {
  const rows = degree?.kis?.contact_hours_by_year;
  if (!Array.isArray(rows) || rows.length === 0) return null;
  // Feb 2026: hide when scheduled_hrs is all-null across years — a
  // bar chart with only "Independent" filled looks broken and tells
  // families nothing they didn't already assume.
  const anyScheduled = rows.some((r) => (r?.scheduled_hrs || 0) > 0);
  if (!anyScheduled) return null;
  const chartRows = rows.map((r) => ({
    year: `Year ${r.year}`,
    Scheduled: r.scheduled_hrs || 0,
    Independent: r.guided_indep_hrs || 0,
    Placement: r.placement_hrs || 0,
  }));
  return (
    <CardShell
      testid="contact-hours-card"
      kicker="Weekly rhythm"
      title="How your time is actually spent"
      subtitle="Hours per year split between timetabled sessions, guided independent study and placements — as published on the Key Information Set."
      footer="Source: KIS (Key Information Set)"
      sourceBlock={degree?.kis}
    >
      <div className="w-full h-[210px]">
        <ResponsiveContainer>
          <BarChart data={chartRows} layout="vertical" margin={{ top: 6, right: 12, bottom: 6, left: 12 }}>
            <XAxis type="number" hide />
            <YAxis dataKey="year" type="category" width={64} tick={{ fontSize: 12, fill: "#334155" }} axisLine={false} tickLine={false} />
            <Tooltip cursor={{ fill: "rgba(0,33,71,0.05)" }} formatter={(v) => `${v} hrs`} />
            <Bar dataKey="Scheduled"  stackId="a" fill={C_PRIMARY}  />
            <Bar dataKey="Independent" stackId="a" fill={C_SUBJECT}  />
            <Bar dataKey="Placement"   stackId="a" fill="#F59E0B"    />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] font-underground-sc tracking-wider text-stone-500 mt-2">
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3" style={{ background: C_PRIMARY }} />Scheduled</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3" style={{ background: C_SUBJECT }} />Independent</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3" style={{ background: "#F59E0B" }} />Placement</span>
      </div>
    </CardShell>
  );
}

// ---------------------------------------------------------------------------
// UK · Assessment split — donut with 3 wedges + benchmark row.
// ---------------------------------------------------------------------------
function AssessmentSplitCard({ degree, benchmarks }) {
  const split = degree?.kis?.assessment_split_pct;
  if (!split) return null;
  // Feb 2026: also hide when the object exists but every value is null
  // (uni page didn't publish the split even though contact hours did).
  const totalPct = Object.values(split).reduce(
    (t, v) => t + (v || 0),
    0,
  );
  if (totalPct < 60) return null;
  const rows = [
    { name: "Written exam", value: split.written_exam || 0, color: C_PRIMARY  },
    { name: "Coursework",   value: split.coursework   || 0, color: C_SUBJECT  },
    { name: "Practical",    value: split.practical    || 0, color: "#F59E0B"  },
  ].filter((r) => r.value > 0);
  const b = benchmarks?.subject?.kis_assessment || benchmarks?.national?.kis_assessment || {};
  return (
    <CardShell
      testid="assessment-split-card"
      kicker="Assessment mix"
      title="How you'll actually be graded"
      subtitle="Share of the year's marks that come from each assessment type. Peer-median row below shows what's typical for this subject."
      footer="Source: KIS (Key Information Set)"
      sourceBlock={degree?.kis}
    >
      <div className="flex items-center gap-4">
        <div className="w-[140px] h-[140px] shrink-0">
          <ResponsiveContainer>
            <PieChart>
              <Pie data={rows} dataKey="value" nameKey="name"
                   cx="50%" cy="50%" innerRadius={38} outerRadius={62}
                   startAngle={90} endAngle={-270} paddingAngle={2}>
                {rows.map((r, i) => <Cell key={i} fill={r.color} />)}
              </Pie>
              <Tooltip formatter={(v) => `${v}%`} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="flex-1 space-y-1.5 text-xs">
          {rows.map((r) => (
            <div key={r.name} className="flex items-center justify-between">
              <span className="inline-flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: r.color }} />
                <span className="text-stone-700">{r.name}</span>
              </span>
              <span className="font-mono">
                {r.value.toFixed(0)}%
                {typeof b[r.name === "Written exam" ? "written_exam" : r.name === "Coursework" ? "coursework" : "practical"] === "number" ? (
                  <span className="text-stone-400 ml-2">
                    (peer: {b[r.name === "Written exam" ? "written_exam" : r.name === "Coursework" ? "coursework" : "practical"].toFixed(0)}%)
                  </span>
                ) : null}
              </span>
            </div>
          ))}
        </div>
      </div>
    </CardShell>
  );
}

// ---------------------------------------------------------------------------
// UK · Ten years out — LEO 10-yr median earnings (GBP).
// Mirrors the US Career10yrCard so UK courses now sit at peer-parity
// on the outcomes-story front.
// ---------------------------------------------------------------------------
const fmtGBP = (n) => (typeof n === "number"
  ? `£${Math.round(n).toLocaleString()}`
  : "—");

function UkCareer10yCard({ degree }) {
  const val10 = degree?.leo?.salary_10y
    ?? degree?.outcomes?.median_earnings_yr10_gbp;
  const val5  = degree?.leo?.salary_5y;
  const s15   = degree?.grad_outcomes_salary;   // 15-month per-course
  if (typeof val10 !== "number" && typeof val5 !== "number" && !s15?.median_gbp) return null;
  const yr = degree?.leo?.salary_10y_year ?? degree?.leo?.salary_5y_year ?? 2024;
  const lq5 = degree?.leo?.salary_5y_lq;
  const uq5 = degree?.leo?.salary_5y_uq;
  const n5  = degree?.leo?.salary_5y_n;
  const scope5 = degree?.leo?.salary_5y_scope;
  const lq10 = degree?.leo?.salary_10y_lq;
  const uq10 = degree?.leo?.salary_10y_uq;
  const n10  = degree?.leo?.salary_10y_n;
  const scope10 = degree?.leo?.salary_10y_scope;
  // Which figure headlines? 5yr per-uni-per-subject beats everything.
  const primary5 = typeof val5 === "number" && (scope5 === "provider_subject" || scope5 === "provider_subject_group");
  const cohort5 = degree?.official_leo5?.rows?.[0]?.cohort;
  const src = {
    data_source: primary5
      ? degree?.leo?.salary_5y_source
      : (typeof val10 === "number" && degree?.leo?.data_source)
        || (s15?.data_source && "Discover Uni GOSALARY 2022-23")
        || degree?.leo?.salary_5y_source,
    data_year:   primary5 && cohort5 ? null : (degree?.leo?.data_year ?? yr),
  };
  const isVerified = !!src.data_source && src.data_source !== "seed_estimate";
  const primary = primary5
    ? { val: val5, lq: lq5, uq: uq5, n: n5, years: 5, scope: scope5, cah2: degree?.leo?.salary_5y_cah2 }
    : { val: val10, lq: lq10, uq: uq10, n: n10, years: 10, scope: scope10 };
  const scopeCopy = (s) => {
    if (s === "provider_subject") return "this uni, this subject";
    if (s === "provider_subject_group") return `this uni, ${degree?.leo?.salary_5y_cah2 || "broader subject group"} (broader subject group)`;
    if (s === "provider_average")  return "this uni, all subjects";
    if (s === "national_subject")  return "national, this subject";
    if (s === "course")            return "this course";
    return "graduate cohort";
  };
  // Build a career-arc timeline: 15m → 5y → 10y (whichever are present).
  const tiers = [];
  if (s15?.median_gbp) tiers.push({
    label: "15 months", val: s15.median_gbp, lq: s15.lq_gbp, uq: s15.uq_gbp,
    n: s15.pop, scope: "course",
  });
  if (typeof val5 === "number" && primary.years !== 5) tiers.push({
    label: "5 years", val: val5, lq: lq5, uq: uq5, n: n5, scope: scope5,
  });
  if (typeof val10 === "number" && primary.years !== 10) tiers.push({
    label: "10 years", val: val10, lq: lq10, uq: uq10, n: n10, scope: scope10,
  });
  return (
    <CardShell
      testid="uk-career-10y-card"
      kicker={`${primary.years} years out`}
      title={`Median earnings ${primary.years} years after graduation`}
      subtitle={
        isVerified
          ? "DfE Longitudinal Education Outcomes (LEO) — matched to HMRC tax returns for every UK graduate. No self-reporting, no survey bias."
          : "HESA's LEO longitudinal — matched to HMRC tax returns, not self-reported. The metric peer sites can't touch."
      }
      footer={
        isVerified && primary.n
          ? `Source: ${src.data_source} · n=${primary.n.toLocaleString()} (${scopeCopy(primary.scope)})`
          : `Source: HESA LEO ${yr}`
      }
      sourceBlock={src}
    >
      <div className="flex items-baseline gap-4">
        <div className="font-serif text-5xl text-[#002147]" data-testid="leo-primary-gbp">{fmtGBP(primary.val)}</div>
        {isVerified && typeof primary.lq === "number" && typeof primary.uq === "number" ? (
          <div className="text-[11px] text-stone-500 leading-tight font-mono">
            <div>LQ <span className="text-stone-800">{fmtGBP(primary.lq)}</span></div>
            <div>UQ <span className="text-stone-800">{fmtGBP(primary.uq)}</span></div>
          </div>
        ) : null}
      </div>
      {/* Primary IQR bar */}
      {isVerified && typeof primary.lq === "number" && typeof primary.uq === "number" ? (
        <div className="mt-4" data-testid="leo-quartile-bar">
          <div className="text-[10px] font-underground-sc tracking-wider text-stone-400 mb-1">
            Middle 50% of grads
          </div>
          <div className="relative h-3 bg-stone-100 rounded">
            {(() => {
              const scale = Math.max(primary.uq * 1.15, 100000);
              const left  = (primary.lq / scale) * 100;
              const width = ((primary.uq - primary.lq) / scale) * 100;
              const median = (primary.val / scale) * 100;
              return (
                <>
                  <div className="absolute inset-y-0 bg-[#002147] rounded"
                       style={{ left: `${left}%`, width: `${width}%` }} />
                  <div className="absolute inset-y-0 w-[2px] bg-[#7C3AED]"
                       style={{ left: `${median}%` }}
                       title={`Median: ${fmtGBP(primary.val)}`} />
                </>
              );
            })()}
          </div>
          <div className="mt-1 flex justify-between text-[10px] text-stone-500 font-mono">
            <span>£0</span>
            <span>£{Math.round(Math.max(primary.uq * 1.15, 100000) / 1000)}k</span>
          </div>
        </div>
      ) : null}
      {/* Career arc — all remaining tiers stacked as compact rows */}
      {tiers.length > 0 ? (
        <div className="mt-4 pt-3 border-t border-stone-100 space-y-2" data-testid="leo-career-arc">
          {tiers.map((t, i) => (
            <div key={i} className="flex items-baseline gap-3" data-testid={`leo-tier-${t.label.replace(/\s+/g, '-')}`}>
              <div className="font-serif text-2xl text-stone-800 min-w-[6rem]">{fmtGBP(t.val)}</div>
              <div className="text-[10px] font-underground-sc tracking-wider text-stone-500 leading-tight">
                <div>{t.label} out · {scopeCopy(t.scope)}</div>
                {typeof t.lq === "number" && typeof t.uq === "number" ? (
                  <div className="normal-case tracking-normal text-stone-500 mt-0.5">
                    LQ {fmtGBP(t.lq)}  ·  UQ {fmtGBP(t.uq)}
                    {t.n ? `  ·  n=${t.n.toLocaleString()}` : ""}
                  </div>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      ) : null}
      <div className="text-[10px] font-underground-sc tracking-wider text-stone-500 mt-3">
        {primary.cah2 ? `${primary.cah2} · ` : ""}{primary.years === 5 && cohort5 ? `graduated ${cohort5}` : `UK graduate cohort ${yr}`}
      </div>
    </CardShell>
  );
}

// ---------------------------------------------------------------------------
// US · Grad rate + retention — twin big numbers.
// ---------------------------------------------------------------------------
function GradRateCard({ degree, benchmarks }) {
  const s = degree?.scorecard;
  if (!s) return null;
  const gr = s.grad_rate_6yr_pct;
  const ret = s.retention_rate_pct;
  if (typeof gr !== "number" && typeof ret !== "number") return null;
  const nat_gr = benchmarks?.national?.grad_rate_6yr_pct;
  const sub_gr = benchmarks?.subject?.grad_rate_6yr_pct;
  const nat_ret = benchmarks?.national?.retention_rate_pct;
  const sub_ret = benchmarks?.subject?.retention_rate_pct;
  const stat = (label, val, sub, nat, tid) => (
    <div className="flex-1 min-w-[160px]" data-testid={tid}>
      <div className="text-[10px] font-underground-sc tracking-wider text-stone-500 mb-1">{label}</div>
      <div className="font-serif text-4xl text-[#002147]">{fmtPct(val, 1)}</div>
      <div className="text-[10px] font-underground-sc tracking-wider text-stone-500 mt-1">
        Subject: <span className="text-stone-800">{fmtPct(sub, 1)}</span> · National: <span className="text-stone-800">{fmtPct(nat, 1)}</span>
      </div>
    </div>
  );
  return (
    <CardShell
      testid="grad-rate-card"
      kicker="Will your kid finish"
      title="Retention & six-year graduation"
      subtitle="Scorecard's completion metrics — the pair peer sites lead with when they talk about a college's 'follow-through'."
      footer="Source: US College Scorecard"
      sourceBlock={degree?.scorecard}
    >
      <div className="flex flex-wrap gap-6">
        {typeof gr  === "number" ? stat("6-yr graduation rate", gr, sub_gr, nat_gr, "grad-rate-stat") : null}
        {typeof ret === "number" ? stat("First-year retention", ret, sub_ret, nat_ret, "retention-stat") : null}
      </div>
    </CardShell>
  );
}

// ---------------------------------------------------------------------------
// US · Net price by income — ladder chart (5 income brackets).
// ---------------------------------------------------------------------------
function NetPriceLadderCard({ degree, benchmarks }) {
  const rows = degree?.scorecard?.net_price_by_income;
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const subj = benchmarks?.subject?.net_price_by_income || [];
  const bySubj = Object.fromEntries((subj || []).map((r) => [r.bracket, r.annual_usd]));
  const chartRows = rows.map((r) => ({
    bracket: r.bracket,
    "This college":  r.annual_usd,
    "Subject median": bySubj[r.bracket] ?? null,
  }));
  return (
    <CardShell
      testid="net-price-ladder-card"
      kicker="Family price tag"
      title="What you'll actually pay by income bracket"
      subtitle="Net price = tuition + room + fees minus grant aid. Scorecard's headline affordability metric — always ask for THIS number, never the sticker."
      footer="Source: US College Scorecard (net-price-by-income)"
      sourceBlock={degree?.scorecard}
    >
      <div className="w-full h-[220px]">
        <ResponsiveContainer>
          <BarChart data={chartRows} layout="vertical" margin={{ top: 4, right: 40, bottom: 4, left: 8 }}>
            <XAxis type="number" hide />
            <YAxis dataKey="bracket" type="category" width={72} tick={{ fontSize: 11, fill: "#334155" }} axisLine={false} tickLine={false} />
            <Tooltip formatter={(v) => fmtUSD(v)} />
            <Bar dataKey="This college" fill={C_PRIMARY}>
              <LabelList dataKey="This college" position="right" formatter={fmtUSD} style={{ fontSize: 10, fill: "#002147" }} />
            </Bar>
            <Bar dataKey="Subject median" fill={C_SUBJECT} fillOpacity={0.35}>
              <LabelList dataKey="Subject median" position="right" formatter={(v) => v == null ? "" : `~${fmtUSD(v)}`} style={{ fontSize: 9, fill: "#7C3AED" }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </CardShell>
  );
}

// ---------------------------------------------------------------------------
// US · Median debt at graduation.
// ---------------------------------------------------------------------------
function MedianDebtCard({ degree, benchmarks }) {
  const val = degree?.scorecard?.median_debt_usd;
  if (typeof val !== "number") return null;
  const nat = benchmarks?.national?.median_debt_usd;
  const sub = benchmarks?.subject?.median_debt_usd;
  const bench = sub ?? nat;
  const delta = typeof bench === "number" ? val - bench : null;
  const arrow = delta == null ? "" : (delta > 500 ? "▲" : (delta < -500 ? "▼" : "▬"));
  // Higher debt = worse — colours are inverted vs retention.
  const arrowClr = delta == null ? "text-stone-500" : (delta > 500 ? "text-red-700" : (delta < -500 ? "text-emerald-700" : "text-stone-500"));
  return (
    <CardShell
      testid="median-debt-card"
      kicker="Debt on graduation day"
      title="Federal loan debt at graduation"
      subtitle="Median debt for students who took federal loans. Not a number peer sites always front-load — but the one families most need to see."
      footer="Source: US College Scorecard"
      sourceBlock={degree?.scorecard}
    >
      <div className="flex items-baseline gap-4">
        <div className="font-serif text-5xl text-[#002147]">{fmtUSD(val)}</div>
        {delta != null ? (
          <div className={`text-sm font-mono ${arrowClr}`} data-testid="debt-delta">
            {arrow} {delta > 0 ? "+" : ""}{fmtUSD(delta)} vs {typeof sub === "number" ? "subject" : "national"} median
          </div>
        ) : null}
      </div>
      <div className="grid grid-cols-2 gap-3 mt-3 text-[11px] font-underground-sc tracking-wider text-stone-500">
        <div>Subject median: <span className="text-stone-800">{fmtUSD(sub)}</span></div>
        <div>National median: <span className="text-stone-800">{fmtUSD(nat)}</span></div>
      </div>
    </CardShell>
  );
}

// ---------------------------------------------------------------------------
// US · 10-year median earnings.
// ---------------------------------------------------------------------------
function Career10yrCard({ degree, benchmarks }) {
  const val = degree?.scorecard?.earnings_10y_usd;
  if (typeof val !== "number") return null;
  const nat = benchmarks?.national?.earnings_10y_usd;
  const sub = benchmarks?.subject?.earnings_10y_usd;
  const bench = sub ?? nat;
  const delta = typeof bench === "number" ? val - bench : null;
  const arrow = delta == null ? "" : (delta > 2000 ? "▲" : (delta < -2000 ? "▼" : "▬"));
  const arrowClr = delta == null ? "text-stone-500" : (delta > 2000 ? "text-emerald-700" : (delta < -2000 ? "text-red-700" : "text-stone-500"));
  return (
    <CardShell
      testid="career-10y-card"
      kicker="Ten years out"
      title="Median earnings 10 years after enrolment"
      subtitle="Scorecard's headline career-value metric — matched to federal tax returns, not self-reported."
      footer="Source: US College Scorecard (field-of-study earnings)"
      sourceBlock={degree?.scorecard}
    >
      <div className="flex items-baseline gap-4">
        <div className="font-serif text-5xl text-[#002147]">{fmtUSD(val)}</div>
        {delta != null ? (
          <div className={`text-sm font-mono ${arrowClr}`} data-testid="career-10y-delta">
            {arrow} {delta > 0 ? "+" : ""}{fmtUSD(delta)} vs {typeof sub === "number" ? "subject" : "national"} median
          </div>
        ) : null}
      </div>
      <div className="grid grid-cols-2 gap-3 mt-3 text-[11px] font-underground-sc tracking-wider text-stone-500">
        <div>Subject median: <span className="text-stone-800">{fmtUSD(sub)}</span></div>
        <div>National median: <span className="text-stone-800">{fmtUSD(nat)}</span></div>
      </div>
    </CardShell>
  );
}

// ---------------------------------------------------------------------------
// US · Equity — Pell / first-gen / racial composition.
// ---------------------------------------------------------------------------
function EquityCard({ degree, benchmarks }) {
  const s = degree?.scorecard;
  if (!s) return null;
  const pell = s.pell_pct;
  const fg = s.first_gen_pct;
  const race = s.race_ethnicity;
  if (typeof pell !== "number" && typeof fg !== "number" && !race) return null;
  const raceRows = race ? [
    { name: "White",    value: race.white_pct    || 0, color: "#334155" },
    { name: "Asian",    value: race.asian_pct    || 0, color: "#0EA5E9" },
    { name: "Hispanic", value: race.hispanic_pct || 0, color: "#F59E0B" },
    { name: "Black",    value: race.black_pct    || 0, color: "#7C3AED" },
    { name: "Other",    value: race.other_pct    || 0, color: "#94A3B8" },
  ].filter((r) => r.value > 0) : [];
  const nat_pell = benchmarks?.national?.pell_pct;
  const nat_fg = benchmarks?.national?.first_gen_pct;
  return (
    <CardShell
      testid="equity-card"
      kicker="Who thrives here"
      title="Access & who's in the room"
      subtitle="Pell (federal need-based aid) & first-generation share are the two proxies peer sites use for 'this campus is not just legacy money'. Racial composition rounds it out."
      footer="Source: US College Scorecard + IPEDS"
      sourceBlock={degree?.scorecard}
    >
      <div className="grid sm:grid-cols-2 gap-6">
        <div className="space-y-3">
          {typeof pell === "number" ? (
            <div data-testid="pell-stat">
              <div className="text-[10px] font-underground-sc tracking-wider text-stone-500">Pell recipients</div>
              <div className="font-serif text-3xl text-[#002147]">{fmtPct(pell, 1)}</div>
              <div className="text-[10px] font-underground-sc tracking-wider text-stone-500">National median: {fmtPct(nat_pell, 1)}</div>
            </div>
          ) : null}
          {typeof fg === "number" ? (
            <div data-testid="firstgen-stat">
              <div className="text-[10px] font-underground-sc tracking-wider text-stone-500">First-generation</div>
              <div className="font-serif text-3xl text-[#002147]">{fmtPct(fg, 1)}</div>
              <div className="text-[10px] font-underground-sc tracking-wider text-stone-500">National median: {fmtPct(nat_fg, 1)}</div>
            </div>
          ) : null}
        </div>
        {raceRows.length ? (
          <div data-testid="race-composition">
            <div className="text-[10px] font-underground-sc tracking-wider text-stone-500 mb-2">Race & ethnicity</div>
            <div className="w-full h-4 flex rounded-sm overflow-hidden mb-2">
              {raceRows.map((r) => (
                <div key={r.name} title={`${r.name}: ${r.value.toFixed(1)}%`} style={{ width: `${r.value}%`, background: r.color }} />
              ))}
            </div>
            <div className="grid grid-cols-2 gap-1 text-[10px] font-underground-sc tracking-wider">
              {raceRows.map((r) => (
                <div key={r.name} className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: r.color }} />
                  <span className="text-stone-700">{r.name}</span>
                  <span className="text-stone-500 ml-auto">{r.value.toFixed(1)}%</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </CardShell>
  );
}

// ---------------------------------------------------------------------------
// UK — Graduate Outcomes activity card
//
// Reads `degree.grad_outcomes_activity` (populated by
// `scripts.ingest_teaching_learning grad_outcomes` from HESA
// Graduate Outcomes Table 1). Renders 15-months-after-graduation
// activity mix: one big "positive outcome" figure, 3 sub-figures,
// and a slim stacked bar for the full breakdown.
// ---------------------------------------------------------------------------
function GradOutcomesActivityCard({ degree }) {
  const g = degree?.grad_outcomes_activity;
  if (!g || typeof g.positive_outcome_pct !== "number") return null;

  const segments = [
    { key: "fte", label: "Full-time employment", value: g.full_time_employment_pct, color: "#002147" },
    { key: "pte", label: "Part-time employment", value: g.part_time_employment_pct, color: "#334E68" },
    { key: "emps", label: "Employment + study",   value: g.employment_and_study_pct, color: "#5F7A99" },
    { key: "fs",  label: "Further study",         value: g.further_study_pct,        color: "#7C3AED" },
    { key: "vol", label: "Voluntary / unpaid",    value: g.voluntary_pct,            color: "#a78bfa" },
    { key: "un",  label: "Unemployed",            value: g.unemployment_pct,         color: "#f97316" },
  ].filter((s) => typeof s.value === "number" && s.value > 0);
  const total = segments.reduce((a, b) => a + b.value, 0);

  return (
    <CardShell
      testid="grad-outcomes-activity-card"
      kicker="15 Months Out"
      title="What grads are doing after they leave"
      subtitle={`HESA Graduate Outcomes — activity of the ${g.cohort} UK undergraduate cohort surveyed 15 months after finishing.`}
      footer={`Source: ${g.data_source} · cohort n=${g.total_known_outcomes?.toLocaleString?.() ?? "—"}`}
      sourceBlock={g}
    >
      <div className="flex items-baseline gap-3 mb-1">
        <div className="font-serif text-5xl text-stone-900 leading-none">
          {fmtPct(g.positive_outcome_pct, 0)}
        </div>
        <div className="text-xs text-stone-500 uppercase tracking-wider font-mono">
          In work or further study
        </div>
      </div>
      <div className="text-[11px] text-stone-500 mb-4">
        (Full-time & part-time work + further study + voluntary work, expressed as % of graduates with a known outcome.)
      </div>

      <div className="grid grid-cols-3 gap-4 mb-4">
        <div data-testid="go-ft-emp">
          <div className="font-mono text-xl text-stone-900">{fmtPct(g.full_time_employment_pct, 1)}</div>
          <div className="text-[10px] uppercase tracking-wider text-stone-500 font-mono">FT employment</div>
        </div>
        <div data-testid="go-fs">
          <div className="font-mono text-xl text-stone-900">{fmtPct(g.further_study_pct, 1)}</div>
          <div className="text-[10px] uppercase tracking-wider text-stone-500 font-mono">Further study</div>
        </div>
        <div data-testid="go-unemp">
          <div className={`font-mono text-xl ${g.unemployment_pct > 8 ? "text-amber-700" : "text-stone-900"}`}>
            {fmtPct(g.unemployment_pct, 1)}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-stone-500 font-mono">Unemployed</div>
        </div>
      </div>

      {/* Stacked activity mix bar */}
      <div className="mb-1 text-[10px] uppercase tracking-wider text-stone-400 font-mono">Activity mix</div>
      <div className="flex h-4 rounded overflow-hidden bg-stone-100" data-testid="go-stacked">
        {segments.map((s) => (
          <div
            key={s.key}
            title={`${s.label}: ${s.value.toFixed(1)}%`}
            className="h-full"
            style={{ width: `${(s.value / (total || 1)) * 100}%`, background: s.color }}
          />
        ))}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-stone-600">
        {segments.map((s) => (
          <span key={s.key} className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-sm" style={{ background: s.color }} />
            {s.label} {s.value.toFixed(1)}%
          </span>
        ))}
      </div>
    </CardShell>
  );
}


// ---------------------------------------------------------------------------
// UK — TEF (Teaching Excellence Framework) card
//
// OfS Gold/Silver/Bronze rating per uni across 3 dimensions:
// Overall, Student experience, Student outcomes.
// ---------------------------------------------------------------------------
const TEF_COLORS = {
  "Gold":                 { bg: "#FDF3D0", fg: "#7A5C00", border: "#D4A72C" },
  "Silver":               { bg: "#F1F4F7", fg: "#3F4A55", border: "#94A3B8" },
  "Bronze":               { bg: "#F6E8DA", fg: "#8B4513", border: "#B87333" },
  "Requires Improvement": { bg: "#FEE2E2", fg: "#991B1B", border: "#DC2626" },
};

function TEFCard({ degree }) {
  const t = degree?.tef;
  if (!t?.overall) return null;
  const rows = [
    { key: "overall",            label: "Overall",            value: t.overall },
    { key: "student_experience", label: "Student experience", value: t.student_experience },
    { key: "student_outcomes",   label: "Student outcomes",   value: t.student_outcomes  },
  ].filter((r) => r.value);
  return (
    <CardShell
      testid="tef-card"
      kicker="TEF 2023"
      title={`OfS awarded this uni ${t.overall} overall`}
      subtitle="Teaching Excellence Framework — the government's own quality rating, based on continuation, outcomes, and student experience evidence submitted by every provider."
      footer={t.outcome_url ? `Source: OfS TEF 2023 · full statement below` : "Source: OfS TEF 2023"}
      sourceBlock={t}
    >
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        {rows.map((r) => {
          const c = TEF_COLORS[r.value] || TEF_COLORS.Silver;
          return (
            <div key={r.key} data-testid={`tef-${r.key}`}
                 className="min-w-0 rounded-lg border-2 p-2 sm:p-3 text-center"
                 style={{ background: c.bg, borderColor: c.border }}>
              <div className="font-serif text-base sm:text-xl leading-none uppercase tracking-wide break-words"
                   style={{ color: c.fg }}>
                {r.value}
              </div>
              <div className="mt-1 text-[9px] uppercase tracking-wider font-mono break-words"
                   style={{ color: c.fg, opacity: 0.75 }}>
                {r.label}
              </div>
            </div>
          );
        })}
      </div>
      {t.outcome_url ? (
        <a href={t.outcome_url}
           target="_blank" rel="noopener noreferrer"
           data-testid="tef-outcome-link"
           className="mt-4 inline-block text-[11px] font-underground-sc tracking-wider text-[#7C3AED] hover:underline">
          Read the full TEF panel statement →
        </a>
      ) : null}
    </CardShell>
  );
}


// ---------------------------------------------------------------------------
// UK · Accreditation card — chartered/PSRB body badges per course
//
// Reads `degree.accreditation_kis.bodies` (Discover Uni). Chartered
// professional/statutory/regulatory-body seals are one of the most
// important-to-applicants signals we surface — e.g. ACCA-recognised
// accounting, IET-accredited engineering, NMC-approved nursing.
// ---------------------------------------------------------------------------
function AccreditationCard({ degree }) {
  const a = degree?.accreditation_kis;
  if (!a?.bodies?.length) return null;
  return (
    <CardShell
      testid="accreditation-card"
      kicker="Professional Recognition"
      title={`Accredited by ${a.count} chartered ${a.count === 1 ? "body" : "bodies"}`}
      subtitle="Professional, statutory, and regulatory bodies (PSRBs) that formally recognise this course. Graduates can typically fast-track membership, exemptions, or licensure with the body."
      footer={`Source: ${a.data_source}`}
      sourceBlock={a}
    >
      <div className="flex flex-wrap gap-2" data-testid="acc-badge-list">
        {a.bodies.map((b, i) => (
          <div key={i}
               data-testid={`acc-badge-${i}`}
               title={b.description || b.body}
               className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-stone-200 bg-stone-50 text-[11px] text-stone-800 hover:border-[#7C3AED] hover:bg-white transition-colors">
            <span className="w-1.5 h-1.5 rounded-full bg-[#7C3AED]" />
            <span className="max-w-[280px] truncate">{b.body}</span>
          </div>
        ))}
      </div>
    </CardShell>
  );
}


// ---------------------------------------------------------------------------
// UK · Job Quality — % of grads in professional/managerial roles
//
// Reads `degree.job_quality` (Discover Uni JOBTYPE, per-course).
// A "quality of employment" signal — high FT employment can hide
// low-skilled work; PROFMAN filters to graduate-level jobs.
// ---------------------------------------------------------------------------
function JobQualityCard({ degree }) {
  const j = degree?.job_quality;
  if (!j || typeof j.professional_managerial_pct !== "number") return null;
  const pm = j.professional_managerial_pct;
  return (
    <CardShell
      testid="job-quality-card"
      kicker="Job Quality"
      title="Are grads in graduate-level jobs?"
      subtitle="% of employed grads in professional or managerial occupations 15 months after finishing. HESA's proxy for 'graduate-level' work — cuts out the lower-skilled roles that would otherwise inflate simple employment %."
      footer={`Source: ${j.data_source} · n=${j.cohort_pop?.toLocaleString?.() ?? "—"} · response ${fmtPct(j.cohort_response_rate_pct, 0)}`}
      sourceBlock={j}
    >
      <div className="flex items-baseline gap-3 mb-3">
        <div className="font-serif text-5xl text-stone-900 leading-none">{fmtPct(pm, 0)}</div>
        <div className="text-xs uppercase tracking-wider text-stone-500 font-mono">Professional or managerial</div>
      </div>
      <div className="flex h-4 rounded overflow-hidden bg-stone-100" data-testid="job-quality-bar">
        <div title={`Professional/managerial: ${pm.toFixed(1)}%`}
             style={{ width: `${pm}%`, background: "#059669" }} />
        <div title={`Other jobs: ${j.other_jobs_pct?.toFixed?.(1) ?? "—"}%`}
             style={{ width: `${j.other_jobs_pct ?? 0}%`, background: "#f97316" }} />
        <div title={`Unknown: ${j.unknown_pct?.toFixed?.(1) ?? "—"}%`}
             style={{ width: `${j.unknown_pct ?? 0}%`, background: "#a8a29e" }} />
      </div>
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-stone-600">
        <span><span className="inline-block w-2 h-2 rounded-sm bg-emerald-600 mr-1" />Professional/managerial {fmtPct(pm, 1)}</span>
        {typeof j.other_jobs_pct === "number" ? (
          <span><span className="inline-block w-2 h-2 rounded-sm bg-orange-500 mr-1" />Other jobs {fmtPct(j.other_jobs_pct, 1)}</span>
        ) : null}
        {typeof j.unknown_pct === "number" ? (
          <span><span className="inline-block w-2 h-2 rounded-sm bg-stone-400 mr-1" />Unknown {fmtPct(j.unknown_pct, 1)}</span>
        ) : null}
      </div>
    </CardShell>
  );
}


// ---------------------------------------------------------------------------
// UK — Graduate Reflections card
//
// HESA "graduate voice" — % of alumni who agree their post-grad
// activity is meaningful and fits their future plans. A distinct
// signal from NSS (which asks about the course, not the outcome).
// ---------------------------------------------------------------------------
function GradReflectionsCard({ degree }) {
  const perCourse = degree?.grad_voice_work;   // Discover Uni — per-course
  const perUni    = degree?.grad_reflections;  // HESA workbook — per-uni
  // Prefer per-course when it has data.
  const isPerCourse = !!perCourse?.meaningful_pct;
  if (!isPerCourse && !perUni?.meaningful?.agree_pct) return null;

  const cohort = isPerCourse ? perCourse.cohort_year : perUni.cohort;
  const source = isPerCourse ? perCourse.data_source : perUni.data_source;
  const scopeCopy = isPerCourse ? "this course" : "this uni";
  const nStr     = isPerCourse
    ? ` · n=${perCourse.pop?.toLocaleString?.() ?? "—"} response ${fmtPct(perCourse.response_rate_pct, 0)}`
    : "";

  // Normalise to a single "tiles" array so the render stays flat.
  const tiles = isPerCourse
    ? [
        { key: "mean",  val: perCourse.meaningful_pct,   label: "Say their activity is meaningful" },
        { key: "track", val: perCourse.on_track_pct,     label: "Say it's on track for their plans" },
        { key: "skill", val: perCourse.skills_used_pct,  label: "Say they use the skills they learned" },
      ]
    : [
        { key: "mean",  val: perUni.meaningful?.agree_pct,           label: "Say their activity is meaningful",
          sub: `${fmtPct(perUni.meaningful?.strongly_agree_pct, 0)} strongly agreed` },
        { key: "track", val: perUni.future_fit?.agree_pct,           label: "Say it fits their future plans",
          sub: `${fmtPct(perUni.future_fit?.strongly_agree_pct, 0)} strongly agreed` },
      ];

  const srcBlock = isPerCourse ? perCourse : perUni;

  return (
    <CardShell
      testid="grad-reflections-card"
      kicker={isPerCourse ? "Graduate Voice · course-specific" : "Graduate Voice"}
      title="Do grads think it was worth it?"
      subtitle={
        isPerCourse
          ? `What ${cohort} alumni of this specific course told HESA 15 months after leaving. Different from NSS — this asks about their life since, not the course itself.`
          : `What ${cohort} alumni told HESA 15 months after leaving. Distinct from NSS — this asks about their life since, not the course itself. Uni-level average (per-course reflections load below when available).`
      }
      footer={`Source: ${source} · ${scopeCopy}${nStr}`}
      sourceBlock={srcBlock}
    >
      <div className={`grid ${tiles.length === 3 ? "grid-cols-3" : "grid-cols-2"} gap-4`}>
        {tiles.filter(t => typeof t.val === "number").map((t) => (
          <div key={t.key} data-testid={`grad-refl-${t.key}`}>
            <div className="font-serif text-3xl text-stone-900 leading-none">
              {fmtPct(t.val, 0)}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-stone-500 font-mono mt-1 min-h-[24px]">
              {t.label}
            </div>
            {t.sub ? (
              <div className="text-[11px] text-stone-500 mt-1">
                <span className="font-mono text-[#7C3AED]">{t.sub}</span>
              </div>
            ) : null}
          </div>
        ))}
      </div>
      {/* Full distribution only when per-uni data (HESA breaks it out); per-course DUI gives % agree only */}
      {!isPerCourse && typeof perUni?.meaningful?.disagree_pct === "number" ? (
        <div className="mt-5">
          <div className="text-[10px] uppercase tracking-wider text-stone-400 font-mono mb-1">
            Full distribution — "activity is meaningful"
          </div>
          <div className="flex h-3 rounded overflow-hidden bg-stone-100" data-testid="grad-refl-dist">
            <div title={`Agree/Strongly agree: ${perUni.meaningful.agree_pct}%`}
                 style={{ width: `${perUni.meaningful.agree_pct}%`, background: "#059669" }} />
            <div title={`Neither: ${perUni.meaningful.neutral_pct}%`}
                 style={{ width: `${perUni.meaningful.neutral_pct}%`, background: "#a8a29e" }} />
            <div title={`Disagree/Strongly disagree: ${perUni.meaningful.disagree_pct}%`}
                 style={{ width: `${perUni.meaningful.disagree_pct}%`, background: "#f97316" }} />
          </div>
          <div className="mt-1 flex gap-3 text-[10px] text-stone-600">
            <span><span className="inline-block w-2 h-2 rounded-sm bg-emerald-600 mr-1" />Agree {fmtPct(perUni.meaningful.agree_pct, 0)}</span>
            <span><span className="inline-block w-2 h-2 rounded-sm bg-stone-400 mr-1" />Neutral {fmtPct(perUni.meaningful.neutral_pct, 0)}</span>
            <span><span className="inline-block w-2 h-2 rounded-sm bg-orange-500 mr-1" />Disagree {fmtPct(perUni.meaningful.disagree_pct, 0)}</span>
          </div>
        </div>
      ) : null}
    </CardShell>
  );
}


// ---------------------------------------------------------------------------
// UK — NSS Equity Card (national sector context, not per-uni)
//
// Reads `/api/nss-equity-gaps` and renders the top-3 biggest gaps
// across student characteristics. Explicit "National context" kicker
// so users don't mistake it for uni-specific data.
// ---------------------------------------------------------------------------
const SUBSCALE_COPY = {
  teaching:     "teaching quality",
  personal_dev: "personal development",
  assessment:   "assessment & feedback",
  support:      "academic support",
  resources:    "learning resources",
  community:    "learning community",
  voice:        "student voice",
  overall:      "overall satisfaction",
};

function NSSEquityCard({ equity }) {
  if (!equity?.characteristics?.length) return null;
  // Top-3 by max_gap size — already sorted server-side.
  const rows = equity.characteristics.slice(0, 3).filter((c) => c.max_gap);
  if (!rows.length) return null;
  const nsssrc = {
    data_source: equity.data_source || `OfS NSS ${equity.year}`,
    data_year:   equity.year,
  };
  return (
    <CardShell
      testid="nss-equity-card"
      kicker="National Context"
      title="Who the NSS treats differently, sector-wide"
      subtitle={`Biggest satisfaction gaps between student groups across every UK undergraduate finalist in ${equity.year}. Sector-level pattern — your university may be more or less equitable than the national picture.`}
      footer={`Source: ${equity.data_source} · UK undergraduates`}
      sourceBlock={nsssrc}
    >
      <div className="space-y-4">
        {rows.map((c) => {
          const mg = c.max_gap;
          const range = 100;
          return (
            <div key={c.key} data-testid={`equity-row-${c.key}`}
                 className="pb-4 border-b border-stone-100 last:border-b-0 last:pb-0">
              <div className="flex items-baseline justify-between mb-1.5">
                <span className="text-xs font-underground-sc tracking-wider text-stone-500">
                  {c.characteristic}
                </span>
                <span className="text-[10px] font-underground-sc tracking-wider text-amber-700 bg-amber-50 rounded-full px-1.5 py-0.5">
                  {mg.delta} pt gap on {SUBSCALE_COPY[mg.subscale] || mg.subscale}
                </span>
              </div>
              <div className="space-y-1.5">
                {c.splits.map((s, i) => {
                  const val = s.subscales?.[mg.subscale];
                  if (typeof val !== "number") return null;
                  const isHigh = s.name === mg.high.name;
                  const isLow  = s.name === mg.low.name;
                  return (
                    <div key={i} data-testid={`equity-split-${c.key}-${i}`}
                         className="flex items-center gap-2">
                      <div className="w-32 flex-shrink-0 text-[11px] text-stone-700 truncate"
                           title={s.name}>
                        {s.name}
                      </div>
                      <div className="flex-1 relative h-3 bg-stone-100 rounded">
                        <div
                          className={`absolute inset-y-0 left-0 rounded ${
                            isHigh ? "bg-emerald-500"
                                   : isLow ? "bg-amber-500"
                                           : "bg-stone-400"
                          }`}
                          style={{ width: `${(val / range) * 100}%` }}
                        />
                      </div>
                      <div className="w-10 text-right font-mono text-[11px] text-stone-800 flex-shrink-0">
                        {val.toFixed(1)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </CardShell>
  );
}


// ---------------------------------------------------------------------------
// Public export — the whole cluster.
// ---------------------------------------------------------------------------
export default function TeachingLearningCluster({ degree, variant = "teaching" }) {
  const [benchmarks, setBenchmarks] = useState(null);
  const [equity, setEquity] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!degree?.id) return;
    axios
      .get(`${API}/degrees/${encodeURIComponent(degree.id)}/teaching-learning-benchmarks`)
      .then((r) => !cancelled && setBenchmarks(r.data))
      .catch(() => !cancelled && setBenchmarks(null));
    return () => { cancelled = true; };
  }, [degree?.id]);

  // Equity data is national UK-only; only fetch when we'll render it (ROI variant).
  useEffect(() => {
    let cancelled = false;
    if (variant !== "roi") return;
    const country = (degree?.country
      || (degree?.university_id?.startsWith("uk-") ? "UK" : "")).toUpperCase();
    if (country !== "UK") return;
    axios
      .get(`${API}/nss-equity-gaps`)
      .then((r) => !cancelled && setEquity(r.data))
      .catch(() => !cancelled && setEquity(null));
    return () => { cancelled = true; };
  }, [degree?.university_id, degree?.country, variant]);

  const country = (degree?.country
    || (degree?.university_id?.startsWith("uk-") ? "UK" : degree?.university_id?.startsWith("us-") ? "US" : "")).toUpperCase();

  // Two rendering variants — the same component powers both sections:
  //   * "teaching"  → the Teaching & Learning cluster (Essential info)
  //   * "roi"       → the Return on Investment cluster (post-grad payoff)
  // Cards return null when their source field is missing so we can just
  // list them and let them self-hide.
  const uk_teaching_cards = [
    <TEFCard           key="tef"      degree={degree}                          />,
    <AccreditationCard key="acc"      degree={degree}                          />,
    <NSSSubscalesCard  key="nss"      degree={degree} benchmarks={benchmarks} />,
    <ContinuationCard  key="cont"     degree={degree} benchmarks={benchmarks} />,
    <ContactHoursCard  key="hours"    degree={degree}                          />,
    <AssessmentSplitCard key="split"  degree={degree} benchmarks={benchmarks} />,
  ];
  const uk_roi_cards = [
    <EarningsTrajectoryCard key="uk-earn-arc" degree={degree} benchmarks={benchmarks} />,
    <UkCareer10yCard   key="uk-c10"   degree={degree}                          />,
    <JobQualityCard    key="job-q"    degree={degree}                          />,
    <GradOutcomesActivityCard key="go" degree={degree}                         />,
    <GradReflectionsCard key="grad-refl" degree={degree}                       />,
    <NSSEquityCard     key="equity"   equity={equity}                          />,
  ];
  const us_cards = [
    <GradRateCard      key="grad"     degree={degree} benchmarks={benchmarks} />,
    <NetPriceLadderCard key="netprice" degree={degree} benchmarks={benchmarks} />,
    <MedianDebtCard    key="debt"     degree={degree} benchmarks={benchmarks} />,
    <Career10yrCard    key="c10"      degree={degree} benchmarks={benchmarks} />,
    <EquityCard        key="equity"   degree={degree} benchmarks={benchmarks} />,
  ];
  const cards = country === "UK"
    ? (variant === "roi" ? uk_roi_cards : uk_teaching_cards)
    : country === "US"
      ? (variant === "roi" ? us_cards : [])   // US "teaching" variant intentionally empty for now
      : [];

  // Filter out cards that self-hid (returned null). If none rendered
  // there's no data — don't paint the section header either.
  const rendered = cards.filter(Boolean).filter((c) => (
    c.key === "equity" && country === "UK"
      ? !!c.props.equity?.characteristics?.length
      : c.props.degree && (
          country === "UK"
            ? (c.props.degree.nss_subscales || c.props.degree.continuation || c.props.degree.kis || c.props.degree.leo?.salary_10y || c.props.degree.leo?.salary_5y || c.props.degree.outcomes?.median_earnings_yr10_gbp || c.props.degree.grad_outcomes_activity || c.props.degree.grad_reflections || c.props.degree.grad_voice_work || c.props.degree.grad_outcomes_salary || c.props.degree.tef || c.props.degree.job_quality || c.props.degree.accreditation_kis)
            : (c.props.degree.scorecard)
        )
  ));

  if (!rendered.length) return null;

  // Cluster metadata per variant. ROI variant uses a distinct section
  // id + kicker copy so the page reads as two separate stories.
  const meta = variant === "roi"
    ? {
        section_id: "return-on-investment",
        testid:     "return-on-investment-cluster",
        kicker:     "Return on Investment",
        title:      "What comes after the degree",
        subtitle_uk: "The four post-graduation signals that actually decide whether this course was worth the fees: verified 10-year earnings, what grads are doing at the 15-month check-in, whether they still think the choice was meaningful, and the equity gaps you should know about before enrolling.",
        subtitle_us: "The post-graduation cost/benefit picture — earnings, activity mix, and the equity gaps behind the sticker price.",
      }
    : {
        section_id: "teaching-learning",
        testid:     "teaching-learning-cluster",
        kicker:     "Teaching & Learning",
        title:      "What studying here actually feels like",
        subtitle_uk: "The four charts every UK guide uses (NSS satisfaction, continuation, KIS contact hours, assessment split) — with subject & national medians so you can tell 'good' from 'great' at a glance.",
        subtitle_us: "College Scorecard's completion, cost and access story — cross-referenced with peers so you can see who this college is actually FOR.",
      };

  return (
    <section id={meta.section_id} data-testid={meta.testid} className="scroll-mt-24 mt-12">
      <div className="mb-6">
        <div className="text-[10px] font-underground-sc tracking-wider text-[#7C3AED] mb-1">
          {meta.kicker}
        </div>
        <h2 className="font-editorial text-3xl text-stone-900 leading-tight">
          {meta.title}
        </h2>
        <p className="text-sm text-stone-500 mt-2 max-w-2xl">
          {country === "UK" ? meta.subtitle_uk : meta.subtitle_us}
        </p>
      </div>
      <div className="grid md:grid-cols-2 gap-6 [&>*]:min-w-0">
        {cards}
      </div>
    </section>
  );
}
